/**
 * What the app knows about merchants, independent of any bank.
 *
 * Three things the import used to do with substring lists written for one
 * person's statements, which went wrong for everyone else:
 *
 * - **Names.** `extractMerchant` cut at the first "*", so every Square, Toast
 *   and DoorDash purchase became "Sq", "Tst", "Dd" — one merchant each, and a
 *   category rule made for one of them recategorised all of them. Digits glued
 *   to a name stayed ("Singaporeair6182473863591"); store numbers and cities
 *   made every branch its own merchant.
 * - **Subscriptions.** Bare substrings: "GYM" matched GYMBOREE, "MEDIUM"
 *   MEDIUM RARE STEAKHOUSE, "DISNEY" a Disneyland ticket, "ADOBE" ADOBE GRILL;
 *   and phone carriers and car insurers were "subscriptions" too.
 * - **Categories for rows the bank didn't categorise** (checking accounts,
 *   statements, Amex's short export). Rules like "IRS → Taxes" matched "FIRST
 *   TECH FED CU", "ATM → Cash" matched ATMOS ENERGY, and a few of the original
 *   developer's own merchants (a landlord, a university) were hard-coded.
 *
 * All three run when a transaction is read (`effectiveCategory`, the Finance
 * view), so rows imported before this are shown right too.
 */
// ─── Names ──────────────────────────────────────────────────────────────────
/** Payment processors that put their own name before the merchant's: "SQ *BLUE BOTTLE". */
const PROCESSOR_PREFIX = /^(?:SQ|TST|DD|SP|PY|PP|PAYPAL|GOOGLE|IC|BT|CKO|LS|PAR|SQU|SUMUP|ZETTLE|CLOVER|SPOT|SPO|EB|FS|WPY|STK|PMT|POS|RAZ|OTTER|PAYU)\s*\*\s*/i;
const STATES = new Set('AL AK AZ AR CA CO CT DE FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY DC PR'.split(' '));
/** Names that arrive in many spellings. */
const ALIASES = [
    [/^(amzn|amazon)(\b| |\.|mktp|mktplace|marketplace|prime)/i, 'Amazon'],
    [/^uber\s*\*?\s*eats/i, 'Uber Eats'],
    [/^uber\b/i, 'Uber'],
    [/^lyft\b/i, 'Lyft'],
    [/^doordash/i, 'DoorDash'],
    [/^netflix/i, 'Netflix'],
    [/^spotify/i, 'Spotify'],
    [/^apple\.com\/bill|^apple\.com|^itunes/i, 'Apple'],
    [/^(google\s*\*?\s*)?youtube/i, 'YouTube'],
    [/^starbucks/i, 'Starbucks'],
    [/^costco/i, 'Costco'],
    [/^wal-?mart|^wm supercenter/i, 'Walmart'],
    [/^target\b/i, 'Target'],
    [/^trader joe/i, "Trader Joe's"],
    [/^whole ?foods|^wfm\b/i, 'Whole Foods'],
];
const titleCase = (s) => s.toLowerCase().replace(/(^|[\s/&(-])([a-z])/g, (_, sep, ch) => sep + ch.toUpperCase());
/**
 * A clean, stable merchant name from a raw description or merchant field.
 * Idempotent: a name it produced comes back unchanged.
 */
export function cleanMerchant(raw) {
    let s = (raw ?? '').trim();
    if (!s)
        return 'Unknown';
    // Fixed-width exports pad the merchant from the city and state with runs of
    // spaces: "STARBUCKS #1234      PORTLAND     OR". The first column is the name.
    // A star joins, though — "UBER   *EATS" is one name, and splitting it
    // first filed every Uber Eats order under Uber rides.
    s = s.replace(/\s*\*\s*/g, ' *').split(/\s{2,}/)[0];
    // "SQ *BLUE BOTTLE COFFEE" → the merchant after the processor.
    if (PROCESSOR_PREFIX.test(s))
        s = s.replace(PROCESSOR_PREFIX, '');
    for (const [re, name] of ALIASES)
        if (re.test(s))
            return name;
    // "UBER *TRIP", "NETFLIX.COM*12345": the name is before the star.
    s = s.replace(/\s*\*.*$/, '');
    // A bank account's ACH line: "Utility Co Web Online 91570628 Web ID: 59407",
    // "Acme Rent PPD ID: 1844372402", "Car Dealer Echk036976". The name is
    // what's before the bank's codes.
    s = s
        .replace(/\b(?:PPD|CCD|WEB|TEL|CTX)\s*ID:?\s*\S+/gi, '')
        .replace(/\bWeb\s+(?:Pmts?|EFT|Online)\b.*$/i, '')
        .replace(/\bEchk\w*/gi, '');
    // A street address glued on or after the name ("FARMERS MAR1111 W EL
    // CAMINO REAL", "UNITED 600 JEFFERSON ST"): from a 3+ digit number followed
    // by two more words, after the first word. And a ", CITY" tail.
    s = s
        .replace(/^([^\d]*?[a-z][^\d]*?)\s?\d{3,6}\s+\S+\s+\S+.*$/i, '$1')
        .replace(/\s+\d+(?:st|nd|rd|th)\s+(?:floor|fl)\b.*$/i, '')
        .replace(/\s*,.*$/, '');
    // Reference codes after the name: a long token mixing letters and digits
    // ("X6Scb5Sbh4Recwr"). Not the first word — that is the name, digits and all.
    s = s.split(/\s+/).filter((w, i) => i === 0 || !(w.length >= 8 && /[a-z]/i.test(w) && (w.match(/\d/g) ?? []).length >= 3)).join(' ');
    s = s
        .replace(/https?:\/\/\S+/gi, '')
        .replace(/\b\d{3}[-.\s]\d{3}[-.\s]\d{4}\b/g, '') // phone numbers
        .replace(/#\s*\d+/g, '') // store numbers
        .replace(/\b(?:store|str|sto|loc|location|unit)\s*\d+\b/gi, '')
        .replace(/\d{4,}/g, ' ') // reference numbers, glued on or not
        .replace(/\.com\b/gi, '')
        .replace(/\s+/g, ' ')
        .trim();
    // A trailing state code, then a lone trailing letter left by truncation
    // ("ALASKA A").
    const words = s.split(' ');
    if (words.length > 1 && STATES.has(words[words.length - 1].toUpperCase()))
        words.pop();
    if (words.length > 1 && /^[a-z]$/i.test(words[words.length - 1]))
        words.pop();
    s = words.join(' ').replace(/[\s\-–,.:;/]+$/, '').trim();
    if (!s)
        return titleCase((raw ?? '').trim().slice(0, 40)) || 'Unknown';
    return titleCase(s).slice(0, 40);
}
/**
 * A transaction's merchant, cleaned. The description first when it carries a
 * processor prefix ("SQ *…"): rows imported before this stored the stub
 * ("Sq") as their merchant, and only the description still has the name.
 */
export function merchantOf(t) {
    const description = t.description ?? '';
    if (PROCESSOR_PREFIX.test(description.trim()))
        return cleanMerchant(description);
    return cleanMerchant(t.merchant || description);
}
// ─── Subscriptions ──────────────────────────────────────────────────────────
/**
 * Digital services billed on repeat — whole words only. Phone carriers,
 * internet and insurance are bills (Bills & Utilities), not subscriptions.
 */
const SUBSCRIPTIONS = [
    [/\bnetflix\b/i, 'Netflix'],
    [/\bspotify\b/i, 'Spotify'],
    [/\bhulu\b/i, 'Hulu'],
    [/\bdisney ?(\+|plus)\b|disneyplus/i, 'Disney+'],
    [/\bhbo ?max\b|\bmax\.com\b/i, 'Max'],
    [/\bparamount ?(\+|plus)\b/i, 'Paramount+'],
    [/\bpeacock\b/i, 'Peacock'],
    [/\bapple ?tv\b/i, 'Apple TV+'],
    [/\bapple\.com\/bill\b|\bicloud\b|\bapple one\b/i, 'Apple services'],
    [/\byoutube ?(premium|tv|music)\b|google \*youtube/i, 'YouTube'],
    [/\baudible\b/i, 'Audible'],
    [/\bkindle unlimited\b/i, 'Kindle Unlimited'],
    [/\bamazon prime\b|\bprime video\b/i, 'Amazon Prime'],
    [/\bgoogle (one|storage)\b/i, 'Google One'],
    [/\bdropbox\b/i, 'Dropbox'],
    [/\b(microsoft|msft|office) ?365\b/i, 'Microsoft 365'],
    [/\badobe (systems|inc|\*|creative)|\badobe\s*\*/i, 'Adobe'],
    [/\bcanva\b/i, 'Canva'],
    [/\bnotion\b/i, 'Notion'],
    [/\bslack\b/i, 'Slack'],
    [/\bzoom\.us\b|\bzoom video\b/i, 'Zoom'],
    [/\bgithub\b/i, 'GitHub'],
    [/\bopenai\b|\bchatgpt\b/i, 'OpenAI'],
    [/\bclaude\.ai\b|\banthropic\b/i, 'Anthropic'],
    [/\bnytimes\b|\bny times\b|\bnew york times\b/i, 'New York Times'],
    [/\bwsj\b|\bwall street journal\b/i, 'Wall Street Journal'],
    [/\blinkedin premium\b/i, 'LinkedIn Premium'],
    [/\bgrammarly\b/i, 'Grammarly'],
    [/\b(lastpass|1password|bitwarden|dashlane)\b/i, 'Password manager'],
    [/\b(nordvpn|expressvpn|surfshark|proton ?vpn)\b/i, 'VPN'],
    [/\bpatreon\b/i, 'Patreon'],
    [/\bsubstack\b/i, 'Substack'],
    [/\bduolingo\b/i, 'Duolingo'],
    [/\bheadspace\b|\bcalm\.com\b/i, 'Meditation app'],
];
/** The subscription service a description names, or null. */
export function subscriptionService(description) {
    const d = description ?? '';
    for (const [re, name] of SUBSCRIPTIONS)
        if (re.test(d))
            return name;
    return null;
}
// ─── Category from the merchant ─────────────────────────────────────────────
/**
 * A category from the merchant alone, for rows the bank gave no category.
 * Whole words and known names; null when nothing is recognisable (the row is
 * then "Other" rather than a guess).
 */
const MERCHANT_CATEGORIES = [
    // Card payments / transfers are classified elsewhere; this is spending.
    [/\b(airline|airways|airfare|lufthansa|aer lingus|air france|air canada|klm|british airways|delta air|united air|american air|alaska air(lines)?|southwest|jetblue|spirit air|frontier air|allegiant|singapore ?air|emirates|qatar|ryanair|easyjet|hawaiian air|indigo|air india|vistara|akasa air)\b/i, 'Travel'],
    [/\b(hotel|airbnb|marriott|hilton|hyatt|sheraton|westin|holiday inn|motel|vrbo|expedia|booking\.com|priceline|hertz|enterprise rent|alamo|avis|budget rent|national car|sixt|turo|amtrak|greyhound)\b/i, 'Travel'],
    [/\b(shell|chevron|exxon|exxonmobil|mobil|arco|texaco|valero|sunoco|speedway|circle k|wawa|quiktrip|marathon petro|phillips 66|conoco|fuel|gas station|parking|parkmobile|toll|fastrak|ez-?pass|chargepoint|uber(?!\s*\*?\s*eats)|lyft|transit|metro|caltrain|bart|trimet|jiffy lube|autozone|o'?reilly auto|napa auto|car wash|dmv)\b/i, 'Transportation'],
    [/\b(safeway|kroger|trader joe'?s?|whole foods|albertsons|winco|fred meyer|publix|aldi|lidl|h-?e-?b|wegmans|sprouts|qfc|new seasons|ralphs|vons|food lion|giant eagle|stop & shop|hy-?vee|meijer|market basket|grocery|groceries|supermarket|instacart)\b/i, 'Groceries'],
    [/\b(restaurant|grill|kitchen|diner|bistro|sushi|ramen|pho|thai|taqueria|tacos?|bbq|pizza|pizzeria|burger|cafe|café|coffee|starbucks|dunkin|peet'?s|dutch bros|mcdonald'?s|chipotle|subway|wendy'?s|taco bell|kfc|chick-?fil-?a|panera|doordash|uber eats|grubhub|postmates|bakery|brewing|brewery|pub|bar & grill)\b/i, 'Food & Dining'],
    [/\b(verizon|t-?mobile|at&t|sprint|mint mobile|cricket|comcast|xfinity|spectrum|cox comm|centurylink|google fi|electric|power|pg&e|edison|energy|water|sewer|trash|waste management|recology|geico|state farm|allstate|progressive|lemonade|usaa|liberty mutual|insurance|conservice|pgande|pg ?and ?e)\b/i, 'Bills & Utilities'],
    // A car bought or financed at a dealer.
    [/\b(hyundai|toyota|honda|nissan|subaru|kia motors|mazda|volkswagen|chevrolet|tesla motors|car dealer|auto sales)\b/i, 'Transportation'],
    [/\b(cvs|walgreens|rite aid|pharmacy|dental|dentist|medical|clinic|hospital|kaiser|doctor|optometr|vision|urgent care|labcorp|quest diag)\b/i, 'Healthcare'],
    [/\b(amazon|target|walmart|costco|best buy|home depot|lowe'?s|ikea|macy'?s|nordstrom|kohl'?s|tj ?maxx|marshalls|ross|old navy|gap|uniqlo|nike|rei|etsy|ebay|wayfair|apple store)\b/i, 'Shopping'],
    [/\b(netflix|spotify|hulu|disney|cinema|theat(er|re)|amc|regal|ticketmaster|stubhub|steam|playstation|xbox|nintendo|concert|museum)\b/i, 'Entertainment'],
    [/\b(rent|apartments?|property management|mortgage|hoa)\b|\bbilt\b.{0,12}\b(rent|housing)\b/i, 'Housing'],
    [/\b(tuition|university|college|school|coursera|udemy)\b/i, 'Education'],
    [/\b(irs|us treasury|tax payment|franchise tax|dept of revenue|department of revenue)\b/i, 'Taxes'],
    [/\b(atm|cash withdrawal)\b|^\s*(\d{1,2}\/\d{1,2}\s+)?withdrawal\s*$/i, 'Cash'],
    [/\b(overdraft|service fee|monthly fee|maintenance fee|late fee|annual fee|foreign transaction fee|interest charge)\b/i, 'Fees & Interest'],
    [/\b(gym|fitness|yoga|salon|barber|spa|petco|petsmart|veterinar)\b/i, 'Personal'],
    [/\b(donation|charity|gofundme|red cross|united way)\b/i, 'Gifts & Donations'],
];
/** A category from the description alone, or null. */
export function guessCategory(description) {
    const d = description ?? '';
    if (subscriptionService(d))
        return 'Subscriptions';
    for (const [re, category] of MERCHANT_CATEGORIES)
        if (re.test(d))
            return category;
    return null;
}
//# sourceMappingURL=finance-merchants.js.map